import { API } from "aws-amplify";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PodSessionByType } from "../../graphql/queries";
import { onUpdatePodSession } from "../../graphql/subscriptions";
import { parsePodSessionNotes } from "../PodV2/notesUtils";
import {
  Box,
  Button,
  Data,
  DataSearch,
  DataSummary,
  DataTable,
  DropButton,
  Text,
  Toolbar,
} from "grommet";
import { FormNext, Google } from "grommet-icons";

const OPEN_BUTTON_WIDTH = "7.5rem";
const DROP_MENU_WIDTH = "10.5rem";

const openButtonStyle = {
  width: OPEN_BUTTON_WIDTH,
  minWidth: OPEN_BUTTON_WIDTH,
  maxWidth: OPEN_BUTTON_WIDTH,
};

const dropMenuButtonStyle = {
  width: DROP_MENU_WIDTH,
  minWidth: DROP_MENU_WIDTH,
  maxWidth: DROP_MENU_WIDTH,
};

const FLAG_COLORS = {
  Green: "#90EE90",
  Yellow: "#FFFF99",
  Orange: "#FFC966",
  Red: "#FFB6B6",
  DNC: "#FFD699",
  RECON: "#FFD699",
  DNS: "#D8BFD8",
  MOD: "#FFB6B6",
};

function enrichSecondarySession(session) {
  const parsedNotes = parsePodSessionNotes(session.notes, session.fields);

  return {
    ...session,
    podId: session.podID ?? session.podId,
    secondaryProctorName: parsedNotes.secondaryProctorName || "",
    flag: parsedNotes.flag || "",
  };
}

function mergeUpdatedSession(sessions, updatedSession) {
  const index = sessions.findIndex((session) => session.id === updatedSession.id);
  if (index === -1) {
    return sessions;
  }

  const next = [...sessions];
  next[index] = enrichSecondarySession(updatedSession);
  return next;
}

function SessionOpenMenu({ sessionId, googleMeetLink }) {
  return (
    <DropButton
      primary
      size="small"
      pad="none"
      style={openButtonStyle}
      dropAlign={{ top: "bottom", left: "left" }}
      dropContent={
        <Box pad="small" gap="small" width={DROP_MENU_WIDTH}>
          <Button
            label="View"
            size="small"
            pad={{ horizontal: "small", vertical: "xsmall" }}
            style={dropMenuButtonStyle}
            onClick={() => window.open(`/pod-session-v2?id=${sessionId}`, "_blank")}
          />
          {googleMeetLink ? (
            <Button
              label="Google Meet"
              size="small"
              pad={{ horizontal: "small", vertical: "xsmall" }}
              style={dropMenuButtonStyle}
              onClick={() => window.open(googleMeetLink, "_blank")}
            />
          ) : null}
        </Box>
      }
    >
      <Box
        direction="row"
        align="center"
        justify="between"
        width="100%"
        pad={{ horizontal: "10px", vertical: "6px" }}
        gap="xsmall"
      >
        <Box direction="row" align="center" gap="xsmall">
          <FormNext size="small" color="white" />
          <Text size="small" color="white">
            Open
          </Text>
        </Box>
        {googleMeetLink ? <Google size="small" color="white" /> : null}
      </Box>
    </DropButton>
  );
}

const columns = [
  {
    property: "actions",
    header: <Text>Actions</Text>,
    render: (datum) => (
      <SessionOpenMenu
        sessionId={datum.id}
        googleMeetLink={datum.googleMeetLink}
      />
    ),
    align: "start",
    alignVertical: "middle",
    size: "small",
  },
  {
    property: "testerName",
    header: <Text>Tester Name</Text>,
    render: (datum) => (
      <Text size="small" truncate>
        {datum.testerName}
      </Text>
    ),
    align: "start",
    alignVertical: "middle",
    size: "medium",
  },
  {
    property: "testerEmail",
    header: <Text>Tester Email</Text>,
    render: (datum) => (
      <Text size="small" truncate>
        {datum.testerEmail}
      </Text>
    ),
    align: "start",
    alignVertical: "middle",
    size: "medium",
  },
  {
    property: "organizationName",
    header: <Text>Organization Name</Text>,
    render: (datum) => (
      <Text size="small" truncate>
        {datum.organizationName}
      </Text>
    ),
    align: "start",
    alignVertical: "middle",
    size: "medium",
  },
  {
    property: "secondaryProctorName",
    header: <Text>Secondary Proctor Name</Text>,
    render: (datum) => (
      <Text size="small" truncate>
        {datum.secondaryProctorName}
      </Text>
    ),
    align: "start",
    alignVertical: "middle",
    size: "medium",
  },
  {
    property: "flag",
    header: <Text>Flag</Text>,
    render: (datum) => {
      if (!datum.flag) {
        return (
          <Text size="small" color="dark-4">
            —
          </Text>
        );
      }

      return (
        <Box
          pad={{ horizontal: "xsmall", vertical: "xxsmall" }}
          background={FLAG_COLORS[datum.flag] || "light-3"}
          round="xsmall"
        >
          <Text size="small" weight="bold">
            {datum.flag}
          </Text>
        </Box>
      );
    },
    align: "start",
    alignVertical: "middle",
    size: "small",
  },
];

const dataProperties = {
  id: {
    label: "ID",
    property: "id",
    filter: false,
    search: false,
  },
  testerName: {
    label: "Tester Name",
    property: "testerName",
    filter: false,
  },
  testerEmail: {
    label: "Tester Email",
    property: "testerEmail",
    filter: false,
  },
  organizationName: {
    label: "Organization Name",
    property: "organizationName",
    filter: true,
  },
  secondaryProctorName: {
    label: "Secondary Proctor Name",
    property: "secondaryProctorName",
    filter: false,
  },
  flag: {
    label: "Flag",
    property: "flag",
    filter: false,
  },
};

export default function PodQueueSecondaries() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchSecondarySessions = useCallback(async () => {
    let allSessions = [];
    let token = null;
    let refetches = 0;

    do {
      const { data } = await API.graphql({
        query: PodSessionByType,
        variables: {
          type: "PodSession",
          sortDirection: "DESC",
          nextToken: token,
          limit: 300,
        },
      });

      const newSessions = data.podSessionByType.items.map(enrichSecondarySession);
      token = data.podSessionByType.nextToken;
      allSessions = [...allSessions, ...newSessions];
      refetches++;
    } while (token && refetches < 4);

    return allSessions;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadSessions = async () => {
      setLoading(true);
      try {
        const fetchedSessions = await fetchSecondarySessions();
        if (!cancelled) {
          setSessions(fetchedSessions);
        }
      } catch (err) {
        console.error("Error fetching secondary sessions:", err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadSessions();

    return () => {
      cancelled = true;
    };
  }, [fetchSecondarySessions]);

  useEffect(() => {
    const subscription = API.graphql({
      query: onUpdatePodSession,
    }).subscribe({
      next: ({ value }) => {
        const updatedSession = value?.data?.onUpdatePodSession;
        if (!updatedSession?.id) {
          return;
        }

        setSessions((currentSessions) =>
          mergeUpdatedSession(currentSessions, updatedSession),
        );
      },
      error: (error) => console.warn("onUpdatePodSession subscription error:", error),
    });

    return () => subscription.unsubscribe();
  }, []);

  const rowProps = useMemo(() => {
    return sessions.reduce((props, session) => {
      if (!session.id) {
        return props;
      }

      props[session.id] = {
        border: {
          side: "bottom",
          size: "xsmall",
          color: "border",
        },
      };

      if (session.status === "FINISHED") {
        props[session.id].background = {
          color: "status-ok",
          opacity: "weak",
        };
      }

      return props;
    }, {});
  }, [sessions]);

  if (loading && sessions.length === 0) {
    return (
      <Box pad="medium">
        <Text>Loading secondary sessions...</Text>
      </Box>
    );
  }

  return (
    <Data data={sessions} properties={dataProperties}>
      <Toolbar>
        <DataSearch placeholder="Search" />
        <DataSummary />
      </Toolbar>
      <DataTable columns={columns} primaryKey="id" rowProps={rowProps} />
    </Data>
  );
}
